import React from 'react';
import { IconButton } from '@interstate/components/IconButton';
import { TrashIcon, ArrowUturnLeftIcon } from '@interstate/components/Icons';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import * as contractingActionCreators from '@makemydeal/dr-dash-store/actions/contractingActionCreators';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { useDispatch, useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { updateTradeInErrorState } from '../lienholderForm/utils';
import { useSingleTradeInFormProvider } from './TradeInFormProvider';

interface TradeInDeleteUndoIconProps {
    formIndex: number;
    isDeleted: boolean;
    isExpanded?: boolean;
    isEditDisabled?: boolean;
}

export const TradeInDeleteUndoIcon: React.FC<TradeInDeleteUndoIconProps> = ({
    formIndex,
    isDeleted,
    isExpanded,
    isEditDisabled
}) => {
    const dispatch = useDispatch();

    const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const tradeInErrors = useSelector((state: StateTree) =>
        dealScreenErrorSelectors.getCategoryValidationErrors(state, DealTabs.TRADE_IN, DealTabs.TRADE_IN)
    );

    const tradeInHasErros = Object.entries(tradeInErrors[formIndex] || {}).some(([_key, value]) => value === true);
    const { tradeInValidator } = useSingleTradeInFormProvider();

    const updateTradeInDeletedState = (isDeleteOperation: boolean) => {
        if (isDealScreenRedDotErrorsEnabled) {
            const updatedErrorState = updateTradeInErrorState(tradeInErrors, formIndex, { isDeleted: isDeleteOperation });
            dispatch(
                dealScreenErrorActionCreators.setErrorFieldsByCategory(DealTabs.TRADE_IN, DealTabs.TRADE_IN, updatedErrorState)
            );
        }
    };

    return !isDeleted ? (
        <IconButton
            aria-label="delete-icon"
            data-testid={`delete-multi-trade-in-btn-${formIndex}`}
            onClick={(e) => {
                e.stopPropagation();
                dispatch(interactionAnalyticsActionCreators.deleteTradeInIcon());
                dispatch(tradeInsActionCreators.deleteTradeIn(formIndex));
                updateTradeInDeletedState(true);
                if (isContractingContext) {
                    dispatch(contractingActionCreators.refreshContractValidationErrors());
                }
            }}
            buttonStyle="secondary"
            disabled={isEditDisabled}
        >
            <TrashIcon />
        </IconButton>
    ) : (
        <IconButton
            aria-label="undo-delete-icon"
            data-testid={`undo-multi-trade-in-btn-${formIndex}`}
            onClick={(e) => {
                e.stopPropagation();
                dispatch(tradeInsActionCreators.undoDeleteTradeIn(formIndex));
                if (tradeInHasErros && !isExpanded) {
                    dispatch(tradeInsActionCreators.toggleTradeInAccordionState(formIndex));
                }
                updateTradeInDeletedState(false);
                tradeInValidator.validateAll();
                tradeInValidator.commit();
            }}
            buttonStyle="secondary"
            disabled={isEditDisabled}
        >
            <ArrowUturnLeftIcon />
        </IconButton>
    );
};
